/**
 * Operations (spec 9.2): the only way data changes on the server, and the unit the
 * device outbox holds. One op per save; a multi-row save produces several ops with
 * distinct HLC ticks. A delete is an op whose `fields` sets `dihapus_pada`.
 *
 * The wire type `Op` is the Zod-inferred shape from ../validators/sinkron so the API,
 * the device and the tests agree on one definition. This module adds the builders.
 */
import {
  contract,
  LINGKUNGAN_SCOPED_TABLES,
  type Peran,
  SCHEMA_VERSION,
  type SyncedTableName,
  syncedTables,
} from '../schema/contract'
import type { Op } from '../validators/sinkron'

export type { Op }

/** A pulled op: what the server stored (spec 9.2, "server adds server_seq, received_at"). */
export interface ServerOp extends Op {
  server_seq: number
  received_at?: string | undefined
}

/** Who is acting on which device; the worker learns it from the main thread after login. */
export interface SyncScope {
  device_id: string
  user_id: string
  peran: Peran
  paroki_id: string | null
  lingkungan_id: string | null
  /** A ketua stasi's stasi (spec 2026-09-30, 7); absent for every other role. */
  stasi_id?: string | null | undefined
}

/** One row change as a screen hands it to the worker: changed columns only. */
export interface LocalWrite {
  table: SyncedTableName
  row_id: string
  fields: Record<string, unknown>
}

export type Row = Record<string, unknown>

const syncedNames: ReadonlySet<string> = new Set(syncedTables.map((t) => t.name))

export function isSyncedTable(name: string): name is SyncedTableName {
  return syncedNames.has(name)
}

export function isLingkunganScoped(table: string): boolean {
  return LINGKUNGAN_SCOPED_TABLES.includes(table)
}

/** A delete is an op that sets `dihapus_pada` to a timestamp (spec 9.2). */
export function isDeleteOp(op: Pick<Op, 'fields'>): boolean {
  const value = op.fields.dihapus_pada
  return typeof value === 'string' && value.length > 0
}

// ---------------------------------------------------------------------------
// UUID v7 (RFC 9562): time-ordered ids generated on the device (spec 8.1)
// ---------------------------------------------------------------------------

// The platform RNG exists in browsers, workers and Node; the es2023 lib does not name it.
declare const crypto: { getRandomValues<T extends ArrayBufferView>(array: T): T }

let lastMs = -1
let lastSeq = 0
const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'))

/**
 * 48-bit millisecond timestamp, 12-bit sequence in rand_a (monotonic within one ms),
 * 62 random bits. Ids from one process therefore sort in creation order.
 *
 * RFC 9562 method 1: the sequence starts each millisecond at a random value with its
 * top bit clear (2048 ids of headroom); should it still overflow, the timestamp is moved
 * one millisecond ahead rather than letting the counter wrap and break the order.
 */
export function uuidv7(now: number = Date.now()): string {
  const bytes = new Uint8Array(16)
  crypto.getRandomValues(bytes)
  let ms = Math.max(now, lastMs)
  if (ms === lastMs) {
    lastSeq += 1
    if (lastSeq > 0x0fff) {
      ms += 1
      lastMs = ms
      lastSeq = (((bytes[6] ?? 0) << 8) | (bytes[7] ?? 0)) & 0x07ff
    }
  } else {
    lastSeq = (((bytes[6] ?? 0) << 8) | (bytes[7] ?? 0)) & 0x07ff
    lastMs = ms
  }
  const high = Math.floor(ms / 0x1_0000_0000)
  const low = ms % 0x1_0000_0000
  bytes[0] = (high >>> 8) & 0xff
  bytes[1] = high & 0xff
  bytes[2] = (low >>> 24) & 0xff
  bytes[3] = (low >>> 16) & 0xff
  bytes[4] = (low >>> 8) & 0xff
  bytes[5] = low & 0xff
  bytes[6] = 0x70 | (lastSeq >>> 8)
  bytes[7] = lastSeq & 0xff
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80
  let out = ''
  for (let i = 0; i < 16; i++) {
    if (i === 4 || i === 6 || i === 8 || i === 10) out += '-'
    out += HEX[bytes[i] ?? 0]
  }
  return out
}

// ---------------------------------------------------------------------------
// Building an op from a local write
// ---------------------------------------------------------------------------

export interface BuildOpInput {
  scope: SyncScope
  write: LocalWrite
  /** The row as the device holds it before the write; null when creating. */
  current: Row | null
  /** The HLC already ticked for this op (one tick per op). */
  hlc: string
  /** ISO timestamp for `dibuat_pada` / `diubah_pada` when the write did not set them. */
  now: string
  opId?: string
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' ? value : null
}

/**
 * Turn a screen's write into the op the outbox stores. `paroki_id` and `lingkungan_id`
 * mirror the row's columns (spec 9.2): the write wins, then the current row, then (for
 * `paroki_id` only) the actor's parish. `_hlc` is never a field. Creation ops carry
 * `id`, `paroki_id`, `dibuat_pada` and `diubah_pada`; every op carries `diubah_pada`.
 */
export function buildOp(input: BuildOpInput): Op {
  const { scope, write, current, hlc, now } = input
  if (!isSyncedTable(write.table)) throw new Error(`buildOp: ${write.table} is not a synced table`)
  const fields: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(write.fields)) {
    if (key === '_hlc' || value === undefined) continue
    fields[key] = value
  }
  const creating = current === null
  const columns = contract[write.table].columns
  if (creating) {
    fields.id = write.row_id
    if (!('paroki_id' in fields)) fields.paroki_id = scope.paroki_id
    if (!('dibuat_pada' in fields)) fields.dibuat_pada = now
  }
  if (!('diubah_pada' in fields) && 'diubah_pada' in columns) fields.diubah_pada = now

  const paroki_id =
    'paroki_id' in fields
      ? stringOrNull(fields.paroki_id)
      : current !== null && 'paroki_id' in current
        ? stringOrNull(current.paroki_id)
        : scope.paroki_id
  let lingkungan_id: string | null = null
  if (isLingkunganScoped(write.table)) {
    lingkungan_id =
      'lingkungan_id' in fields
        ? stringOrNull(fields.lingkungan_id)
        : stringOrNull(current?.lingkungan_id)
  }
  return {
    op_id: input.opId ?? uuidv7(),
    device_id: scope.device_id,
    user_id: scope.user_id,
    paroki_id,
    lingkungan_id,
    table: write.table,
    row_id: write.row_id,
    hlc,
    schema_version: SCHEMA_VERSION,
    fields,
  }
}

/**
 * An op that creates its row, with the envelope's `paroki_id` (and, on the four scoped
 * tables, its `lingkungan_id`) as fields when the op does not set them. The row's parish
 * and lingkungan are what the stored fields say (spec 9.2), while push authorization
 * checks the envelope (steps 2, 3a) and pull routes by it; a creation that left them out
 * was stored with NULL, and a NULL-parish row is diocese reference data every device in
 * the diocese receives. buildOp always sets both on a creation, so only a crafted op
 * changes. A field the op sets is never replaced (authorization refuses one that differs),
 * and a NULL envelope adds nothing. The server applies it before checking an op that finds
 * no row, so the checks, the op log and the row agree; never to an update, whose envelope
 * lingkungan may trail a move made elsewhere.
 */
export function withEnvelopeFields(op: Op): Op {
  const extra: Record<string, unknown> = {}
  if (op.fields.paroki_id === undefined && op.paroki_id !== null) extra.paroki_id = op.paroki_id
  if (
    isLingkunganScoped(op.table) &&
    op.fields.lingkungan_id === undefined &&
    op.lingkungan_id !== null
  ) {
    extra.lingkungan_id = op.lingkungan_id
  }
  return Object.keys(extra).length === 0 ? op : { ...op, fields: { ...op.fields, ...extra } }
}

/** The delete write for a row: a soft delete stamped with `now` (spec 9.2). */
export function deleteWrite(table: SyncedTableName, rowId: string, now: string): LocalWrite {
  return { table, row_id: rowId, fields: { dihapus_pada: now } }
}

// ---------------------------------------------------------------------------
// Size, for the 1 MB push limit (spec 9.6)
// ---------------------------------------------------------------------------

/** UTF-8 byte length without TextEncoder, which the es2023 lib does not declare. */
export function utf8Length(text: string): number {
  let n = 0
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i)
    if (c < 0x80) n += 1
    else if (c < 0x800) n += 2
    else if (c >= 0xd800 && c <= 0xdbff) {
      n += 4
      i++
    } else n += 3
  }
  return n
}

/** Bytes an op occupies in a JSON push body. */
export function opBytes(op: Op): number {
  return utf8Length(JSON.stringify(op))
}
